import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { DocumentService } from "@/services/DocumentService";
import { useFileDownload } from "@/composables/lists/useFileDownload";

/**
 * @composable useHomeDocuments
 * @description Gestisce il caricamento e il download dei documenti recenti per la Home Page.
 * @returns {{
 *   recentDocuments: import('vue').Ref<Array>,
 *   loaded: import('vue').Ref<boolean>,
 *   hasError: import('vue').Ref<boolean>,
 *   downloadingId: import('vue').Ref<number|null>,
 *   loadRecentDocuments: Function,
 *   downloadDocument: Function
 * }}
 */
export function useHomeDocuments() {
  const { locale } = useI18n();
  const recentDocuments = ref([]);     // → Array<Object>: ultimi 5 documenti dall'archivio (campo `read` indica se già letto)
  const loaded = ref(false);           // → Boolean: true quando il caricamento è completato (convenzione loaded=true → dati disponibili)
  const hasError = ref(false);         // → Boolean: true se il caricamento è fallito (diverso da "nessun documento")

  const { downloadingId, download } = useFileDownload(DocumentService.downloadDocument);

  watch(locale, loadRecentDocuments);

  async function loadRecentDocuments() {
    loaded.value = false;
    hasError.value = false;
    const { data, error } = await DocumentService.getDocuments({
      page_size: 5,
      page_number: 1,
      language: locale.value,
    });

    if (error) {
      console.error(error);
      hasError.value = true;
      recentDocuments.value = [];
    } else {
      recentDocuments.value = data?.items ?? [];
    }
    loaded.value = true;
  }

  async function downloadDocument(doc) {
    const ok = await download(doc, { id: doc.id, args: [doc.id, doc.category?.id], fileName: doc.name });
    // scaricato: il backend lo segna come letto, il badge passa subito da NEW a Scaricato senza ricaricare la lista
    if (ok) doc.read = true;
    return ok;
  }

  return {
    recentDocuments,
    loaded,
    hasError,
    downloadingId,
    loadRecentDocuments,
    downloadDocument,
  };
}
